import { ReactElement, useEffect, useState } from "react";
import Input from "../../../../components/Atomos/Inputs/Input";
import InputLabel from "../../../../components/Atomos/Inputs/inputLabel";
import SwitchToggle from "../../../../components/Atomos/Switch-Toogle/Switch";
import NewButton from "../../../../components/Atomos/Buttons/newButton";
import { EmailClient, IEmailSettings } from "../../../../interfaces/models/IEmailSettings";
import { getEmailSettings, saveEmailSettings } from "../../../../Services/EmailSettings";
import { toastError, toastSuccess } from "../../../../utils/toast";
import "../../../../assets/css/PageSurface.css";
import "../../../../assets/css/Text.css";
import "../../../../assets/css/InputText.css";

type EmailForm = {
  client: EmailClient;
  server: string;
  port: string;
  user: string;
  password: string;
  useSSL: boolean;
  trustSelfSignedCert: boolean;
  shieldRecipients: boolean;
  passwordConfigured: boolean;
};

const EMPTY_FORM: EmailForm = {
  client: "azureAcs",
  server: "",
  port: "587",
  user: "",
  password: "",
  useSSL: false,
  trustSelfSignedCert: false,
  shieldRecipients: true,
  passwordConfigured: false,
};

function wholePort(value: string): number | undefined {
  const port = Number(value);
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return undefined;
  }
  return port;
}

function formFromSettings(settings: IEmailSettings): EmailForm {
  return {
    client: settings.client === "smtp" ? "smtp" : "azureAcs",
    server: settings.server ?? "",
    port: String(settings.port ?? 587),
    user: settings.user ?? "",
    password: "",
    useSSL: settings.useSSL === true,
    trustSelfSignedCert: settings.trustSelfSignedCert === true,
    shieldRecipients: settings.shieldRecipients !== false,
    passwordConfigured: settings.passwordConfigured === true,
  };
}

export default function EmailSettings(): ReactElement {
  const [form, setForm] = useState<EmailForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let active = true;
    getEmailSettings()
      .then((settings) => {
        if (active) {
          setForm(formFromSettings(settings));
        }
      })
      .catch((error: unknown) => {
        if (active) {
          setLoadError(error instanceof Error ? error.message : "Could not load email settings.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  function patch(next: Partial<EmailForm>): void {
    setForm((current) => ({ ...current, ...next }));
  }

  async function save(): Promise<void> {
    const port = wholePort(form.port);
    if (form.client === "smtp" && port == null) {
      toastError("Port must be a whole number from 1 to 65535.");
      return;
    }
    const payload: IEmailSettings = {
      id: "email",
      client: form.client,
      server: form.server.trim(),
      port: port ?? 587,
      user: form.user.trim(),
      useSSL: form.useSSL,
      trustSelfSignedCert: form.trustSelfSignedCert,
      shieldRecipients: form.shieldRecipients,
      passwordConfigured: form.passwordConfigured,
    };
    if (form.password) {
      payload.password = form.password;
    }
    setSaving(true);
    try {
      const saved = await saveEmailSettings(payload);
      setForm(formFromSettings(saved));
      toastSuccess("Email settings saved.");
    } catch (error: unknown) {
      toastError(error instanceof Error ? error.message : "Could not save email settings.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p>Loading email settings…</p>;
  }
  if (loadError) {
    return <p>{loadError}</p>;
  }

  return (
    <section className="stack max-w-xl">
      <h3>Email</h3>
      <div className="stack-tight">
        <InputLabel name="emailClient" title="Client" required />
        <select
          id="emailClient"
          name="emailClient"
          className="field-control"
          value={form.client}
          onChange={(event) => patch({ client: event.target.value as EmailClient })}
        >
          <option value="azureAcs">Azure ACS</option>
          <option value="smtp">SMTP</option>
        </select>
      </div>
      {form.client === "smtp" ? <SmtpFields form={form} onChange={patch} /> : null}
      <div>
        <NewButton
          text={saving ? "Saving…" : "Save"}
          color="accent"
          size="medium-no-icon"
          disabled={saving}
          onClick={() => {
            void save();
          }}
        />
      </div>
    </section>
  );
}

function SmtpFields({ form, onChange }: { form: EmailForm; onChange: (next: Partial<EmailForm>) => void }): ReactElement {
  return (
    <div className="stack">
      <Input title="Server" name="server" value={form.server} error={false} required onChange={(event) => onChange({ server: event.target.value })} />
      <Input title="Port" name="port" type="number" value={form.port} error={false} required onChange={(event) => onChange({ port: event.target.value })} />
      <Input title="User" name="user" value={form.user} error={false} required onChange={(event) => onChange({ user: event.target.value })} />
      <Input
        title="Password"
        name="password"
        type="password"
        value={form.password}
        error={false}
        required={!form.passwordConfigured}
        placeholder={form.passwordConfigured ? "Leave blank to keep the current password" : ""}
        onChange={(event) => onChange({ password: event.target.value })}
      />
      <SwitchToggle name="useSSL" title="Use SSL" checked={form.useSSL} seter={(checked) => onChange({ useSSL: checked })} switchFirst />
      <SwitchToggle name="trustSelfSignedCert" title="Trust self-signed certificate" checked={form.trustSelfSignedCert} seter={(checked) => onChange({ trustSelfSignedCert: checked })} switchFirst />
      <SwitchToggle name="shieldRecipients" title="Shield recipients" checked={form.shieldRecipients} seter={(checked) => onChange({ shieldRecipients: checked })} switchFirst />
    </div>
  );
}
